import { fetchClient } from '@/api/client';
import { body } from '@/api/call';
import type { components } from '@/api/client';
import type { DownloadAdapter, DownloadLocator, DownloadSnapshot } from '@/components/downloads';

type Archive = components['schemas']['DatasetArchiveOut'];
function archiveSnapshot(result:Archive,project:string):DownloadSnapshot {
  return {locator:{kind:'dataset_archive',project,id:result.archive_id},remoteState:result.state,attempt:result.attempt,progress:result.progress,
    artifact:result.artifact?{filename:result.artifact.filename,sizeBytes:result.artifact.size_bytes,expiresAt:result.artifact.expires_at}:null,
    error:result.error_code?{code:result.error_code,message:result.error_message??'Preparation failed.'}:null,canRetry:result.can_retry,canCancel:false};
}
export const datasetArchiveAdapter:DownloadAdapter={
  async observe(pin,signal){
    if(pin.kind==='view_export'||pin.kind==='model_export')throw new Error('Invalid archive locator');
    const result=pin.kind==='dataset_archive_request'
      ?await body(fetchClient.GET('/api/projects/{slug}/dataset-archive-requests/{request_key}',{params:{path:{slug:pin.project,request_key:pin.requestKey}},signal}))
      :await body(fetchClient.GET('/api/projects/{slug}/dataset-archives/{archive_id}',{params:{path:{slug:pin.project,archive_id:pin.id}},signal}));
    return archiveSnapshot(result,pin.project);
  },
  async retry(pin){
    if(pin.kind!=='dataset_archive')return pin;
    const result=await body(fetchClient.POST('/api/projects/{slug}/dataset-archives/{archive_id}/retry',{params:{path:{slug:pin.project,archive_id:pin.id}}}));
    return {kind:'dataset_archive',project:pin.project,id:result.archive_id};
  },
  async access(pin){
    if(pin.kind!=='dataset_archive')throw new Error('Archive is not ready');
    const result=await body(fetchClient.POST('/api/projects/{slug}/dataset-archives/{archive_id}/access',{params:{path:{slug:pin.project,archive_id:pin.id}}}));
    return {url:result.url,headers:result.headers,filename:result.filename,sha256:result.sha256,sizeBytes:result.size_bytes,expiresAt:result.expires_at};
  },
};
function viewPin(pin:DownloadLocator){if(pin.kind!=='view_export')throw new Error('Invalid export locator');return pin;}
export const datasetViewExportAdapter:DownloadAdapter={
  async observe(value,signal){
    let pin=viewPin(value);
    const pending=(error:DownloadSnapshot['error']=null):DownloadSnapshot=>({locator:pin,remoteState:error?'failed':'building',attempt:0,progress:null,artifact:null,error,canRetry:!!error,canCancel:false});
    if(!pin.exportId){
      if(pin.materializeJobId){
        const {job}=await body(fetchClient.GET('/api/jobs/{job_id}',{params:{path:{job_id:pin.materializeJobId}},signal}));
        if(['failed','cancelled','lost'].includes(job.status))return pending({code:'materialization_failed',message:job.error??`File preparation ${job.status}.`});
        if(job.status!=='done')return pending();
      }
      if(!pin.treeId&&!pin.materializeJobId)throw new Error('The exact file preparation operation is unavailable.');
      const result=await body(fetchClient.POST('/api/views/{view_id}/exports',{params:{path:{view_id:pin.viewId}},body:{format:pin.format as 'yolo'|'coco'|'imagefolder',...(pin.treeId?{tree_id:pin.treeId}:{materialize_job_id:pin.materializeJobId})},signal}));
      pin={...pin,exportId:result.export_id};
    }
    const result=await body(fetchClient.GET('/api/view-exports/{view_export_id}',{params:{path:{view_export_id:pin.exportId!}},signal}));
    return {locator:pin,remoteState:result.state,attempt:result.attempt,progress:result.progress,artifact:result.artifact?{filename:`dataset-${pin.format}.zip`,sizeBytes:result.artifact.size_bytes}:null,error:result.error_code?{code:result.error_code,message:result.error_message??'Preparation failed.'}:null,canRetry:result.state==='failed',canCancel:false};
  },
  async retry(value){
    const pin=viewPin(value);
    if(pin.exportId){await body(fetchClient.POST('/api/view-exports/{view_export_id}/retry',{params:{path:{view_export_id:pin.exportId}},body:{}}));return pin;}
    const result=await body(fetchClient.POST('/api/views/{view_id}/materialize',{params:{path:{view_id:pin.viewId}},body:{format:pin.format as 'yolo'|'coco'|'imagefolder'}}));
    return {...pin,treeId:result.tree?.id,materializeJobId:result.tree?undefined:result.job_id??undefined};
  },
  async access(value){
    const pin=viewPin(value);if(!pin.exportId)throw new Error('Archive is not ready');
    const access=await body(fetchClient.POST('/api/view-exports/{view_export_id}/access',{params:{path:{view_export_id:pin.exportId}},body:{}}));
    return {url:`/api/view-exports/${pin.exportId}/file`,headers:{},filename:`dataset-${pin.format}.zip`,sha256:access.sha256,sizeBytes:access.size_bytes,expiresAt:access.expires_at};
  },
};
export const datasetDownloadAdapters={dataset_archive:datasetArchiveAdapter,dataset_archive_request:datasetArchiveAdapter,view_export:datasetViewExportAdapter};
